import React from "react";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { useTelegramSafeAreaInsets } from "utils/hooks/useTelegramWebApp.ts";
import { BOTTOM_NAVIGATOR_HEIGHT } from "common/constants.ts";
import { BulkCancelProgressData } from "@shared/types/common/jobs.types";
import { useDraggable } from "utils/hooks/useDraggable";
import { useTheme } from "styled-components";
import Text from "common/Typography/Text.tsx";

interface BulkProgressMinimizedIndicatorProps {
  jobData: BulkCancelProgressData | null;
  total: number;
  isError?: boolean;
  tradesData?: BulkCancelProgressData | null;
  tradesTotal?: number;
  isTradesError?: boolean;
  onClick: () => void;
}

const BulkProgressMinimizedIndicator: React.FC<
  BulkProgressMinimizedIndicatorProps
> = ({
  jobData,
  total,
  isError = false,
  tradesData,
  tradesTotal = 0,
  isTradesError = false,
  onClick,
}) => {
  const hasDoubleRing = tradesData !== undefined;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { safeAreaInset, contentSafeAreaInset } = useTelegramSafeAreaInsets();
  const theme = useTheme();

  const size = isBottomNavigator ? MOBILE_SIZE : SIZE;
  const stroke = isBottomNavigator ? MOBILE_STROKE : STROKE;
  const outerR = isBottomNavigator ? MOBILE_OUTER_R : OUTER_R;
  const innerR = isBottomNavigator ? MOBILE_INNER_R : INNER_R;
  const cx = isBottomNavigator ? MOBILE_CX : CX;
  const cy = isBottomNavigator ? MOBILE_CY : CY;
  const percentFontSize = isBottomNavigator ? 12 : 14;
  const countFontSize = isBottomNavigator ? 8 : 11;

  const defaultBottom =
    14 +
    (isBottomNavigator ? BOTTOM_NAVIGATOR_HEIGHT : 0) +
    (safeAreaInset.bottom ?? 0) +
    (contentSafeAreaInset.bottom ?? 0);

  const { position, isDragging, wasDragged, dragHandlers } = useDraggable({
    size,
    boundary: {
      bottom: defaultBottom,
      top: (safeAreaInset.top ?? 0) + (contentSafeAreaInset.top ?? 0),
    },
  });

  const botsPercent = calcPercent(jobData, total);
  const tradesPercent = hasDoubleRing
    ? calcPercent(tradesData ?? null, tradesTotal)
    : 0;

  const completed =
    (jobData?.done ?? 0) +
    (jobData?.failed ?? 0) +
    (jobData?.inactive_market ?? 0);

  const outerColor = isError
    ? "#ff4d4f"
    : hasDoubleRing
    ? "#14CB17"
    : theme.isNewLook
    ? theme.colors.primaryBlue
    : "#3272FE";

  const innerColor = isTradesError
    ? isError
      ? "#ff7a45"
      : "#ff4d4f"
    : theme.isNewLook
    ? theme.colors.primaryBlue
    : "#3272FE";

  const handleClick = () => {
    if (!wasDragged()) {
      onClick();
    }
  };

  return (
    <div
      {...dragHandlers}
      onTouchStart={(e) => e.stopPropagation()}
      onClick={handleClick}
      style={{
        position: "absolute",
        ...(position.mode === "anchor"
          ? { bottom: position.bottom, right: position.right }
          : { left: position.x, top: position.y }),
        width: size,
        height: size,
        cursor: isDragging ? "grabbing" : "grab",
        zIndex: 9999999999,
        pointerEvents: "all",
        filter: isDragging
          ? "drop-shadow(0 8px 24px rgba(0,0,0,0.6))"
          : "drop-shadow(0 4px 12px rgba(0,0,0,0.4))",
        transform: isDragging ? "scale(1.08)" : "scale(1)",
        transition: isDragging
          ? "filter 0.15s ease, transform 0.15s ease"
          : "filter 0.2s ease, transform 0.2s ease",
        userSelect: "none",
      }}
      title="Click to restore progress"
    >
      <svg width={size} height={size} style={{ display: "block" }}>
        {/* Background */}
        <circle
          cx={cx}
          cy={cy}
          r={size / 2}
          fill={theme.isDarkMode ? "#0F1115" : theme.colors.cardBgColor}
        />

        {/* Outer ring */}
        <Ring
          radius={outerR}
          percent={botsPercent}
          color={outerColor}
          trailColor={TRAIL_COLOR}
          cx={cx}
          cy={cy}
          stroke={stroke}
        />

        {/* Inner ring (bots variant only) */}
        {hasDoubleRing && (
          <Ring
            radius={innerR}
            percent={tradesPercent}
            color={innerColor}
            trailColor={TRAIL_COLOR}
            cx={cx}
            cy={cy}
            stroke={stroke}
          />
        )}
      </svg>

      {/* Center text */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: size,
          height: size,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        <Text
          style={{
            fontSize: percentFontSize,
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {botsPercent}%
        </Text>
        <Text
          type="secondary"
          style={{ fontSize: countFontSize, lineHeight: 1.2, fontWeight: 600 }}
        >
          {completed} / {total}
        </Text>
      </div>
    </div>
  );
};

export default BulkProgressMinimizedIndicator;

interface RingProps {
  radius: number;
  percent: number;
  color: string;
  trailColor: string;
  cx: number;
  cy: number;
  stroke: number;
}

const Ring: React.FC<RingProps> = ({
  radius,
  percent,
  color,
  trailColor,
  cx,
  cy,
  stroke,
}) => {
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <g>
      {/* Trail */}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke={trailColor}
        strokeWidth={stroke}
        strokeLinecap="butt"
      />
      {/* Progress */}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="butt"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: "stroke-dashoffset 0.4s ease" }}
      />
    </g>
  );
};

function calcPercent(
  data: BulkCancelProgressData | null,
  total: number
): number {
  if (!data || total === 0) return 0;
  const completed =
    (data.done ?? 0) + (data.failed ?? 0) + (data.inactive_market ?? 0);
  return Math.round((completed / total) * 100);
}

const TRAIL_COLOR = "rgba(255,255,255,0.10)";
const SIZE = 76;
const STROKE = 3;
const GAP = 1;

const MOBILE_SIZE = 60;
const MOBILE_STROKE = 2.5;
const MOBILE_GAP = 0.5;

const OUTER_R = SIZE / 2 - STROKE / 2;
const INNER_R = SIZE / 2 - STROKE - GAP - STROKE / 2;

const CX = SIZE / 2;
const CY = SIZE / 2;

const MOBILE_OUTER_R = MOBILE_SIZE / 2 - MOBILE_STROKE / 2;
const MOBILE_INNER_R =
  MOBILE_SIZE / 2 - MOBILE_STROKE - MOBILE_GAP - MOBILE_STROKE / 2;
const MOBILE_CX = MOBILE_SIZE / 2;
const MOBILE_CY = MOBILE_SIZE / 2;
